import { useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Plant, SignIn } from '@phosphor-icons/react';
import { useAuth } from '@/context/AuthContext';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const formatError = (detail) => {
  if (!detail) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(' ');
  return String(detail);
};

const Login = () => {
  const { user, login } = useAuth();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  if (user === undefined) return null; // still loading
  if (user) return <Navigate to={location.state?.from || '/'} replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const u = await login(email.trim().toLowerCase(), password);
      toast.success(`Welcome ${u.role === 'owner' ? 'Owner' : 'Team'}!`);
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B2818] flex items-center justify-center p-4" data-testid="login-page">
      <div className="w-full max-w-md bg-[#0F3622] border-2 border-[#A3E635] rounded-md shadow-[6px_6px_0px_0px_rgba(163,230,53,0.4)] p-8">
        <div className="flex items-center gap-3 mb-6">
          <Plant size={48} weight="duotone" className="text-[#A3E635]" />
          <div>
            <h1 className="text-3xl font-black text-white tracking-tight" style={{fontFamily: 'Cabinet Grotesk, sans-serif'}}>Tulsi Jobs</h1>
            <p className="text-xs text-white font-medium uppercase tracking-wider">Placement CRM Login</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="email" className="text-xs font-bold uppercase tracking-wider text-white">Email</Label>
            <Input
              id="email"
              data-testid="login-email"
              type="email"
              required
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="bg-white border-2 border-[#A3E635] rounded-md px-3 py-2 text-black focus:outline-none focus:shadow-[4px_4px_0px_0px_rgba(163,230,53,0.6)] transition-shadow"
            />
          </div>
          <div>
            <Label htmlFor="password" className="text-xs font-bold uppercase tracking-wider text-white">Password</Label>
            <Input
              id="password"
              data-testid="login-password"
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="bg-white border-2 border-[#A3E635] rounded-md px-3 py-2 text-black focus:outline-none focus:shadow-[4px_4px_0px_0px_rgba(163,230,53,0.6)] transition-shadow"
            />
          </div>

          {error && (
            <div data-testid="login-error" className="bg-[#F87171] text-black border-2 border-black rounded-md px-3 py-2 text-sm font-bold">
              {error}
            </div>
          )}

          <Button
            type="submit"
            data-testid="login-submit"
            disabled={submitting}
            className="w-full bg-[#A3E635] text-black border-2 border-[#A3E635] rounded-md font-bold px-4 py-3 shadow-[3px_3px_0px_0px_rgba(163,230,53,0.5)] hover:-translate-y-0.5 hover:shadow-[5px_5px_0px_0px_rgba(163,230,53,0.7)] transition-all active:translate-y-0.5 active:shadow-none"
          >
            <SignIn size={20} weight="bold" className="mr-2" />
            {submitting ? 'Signing in…' : 'Sign In'}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default Login;
